import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaPalette, FaStar, FaCheckCircle, FaSpinner, FaCheck, FaMagic, FaExclamationTriangle, FaExternalLinkAlt, FaGlobe } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { eventThemeApi } from '../../api/themes/eventThemeApi';
import { useAuthContext } from '../../context/AuthContext';
import './styles/Website-Templates.css';

const WebsiteTemplates = () => {
    const { currentEvent } = useAuthContext();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeWebsiteId, setActiveWebsiteId] = useState(null);
    const [submittingId, setSubmittingId] = useState(null);

    const fetchCustomWebsites = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch custom website options from GET /api/event/custom-websites
            const res = await eventThemeApi.getCustomWebsites();
            const rawList = res?.data?.data?.data || res?.data?.data || res?.data?.custom_websites || res?.data || res || [];

            if (Array.isArray(rawList)) {
                const formatted = rawList.map((item, idx) => ({
                    id: item.id || idx + 1,
                    name: item.name || item.title || item.template_name || `Custom Website #${item.id || idx + 1}`,
                    tag: item.tag || item.theme_tag || item.style || 'Custom Theme',
                    category: item.category || item.theme_category || 'Website Template',
                    description: item.description || item.details || '',
                    image: item.image || item.thumbnail || item.preview_image || null,
                    preview_url: item.preview_url || item.url || item.demo_url || null,
                    status: item.status
                }));
                setTemplates(formatted);

                // Set active website ID if present in active event / currentEvent
                if (currentEvent?.custom_website_id) {
                    setActiveWebsiteId(currentEvent.custom_website_id);
                }
            } else {
                setTemplates([]);
            }
        } catch (err) {
            console.error("Error loading custom websites from API:", err);
            setError("Imeshindwa kupakia custom website options kutoka kwenye API.");
        } finally {
            setLoading(false);
        }
    }, [currentEvent?.custom_website_id]);

    useEffect(() => {
        fetchCustomWebsites();
    }, [fetchCustomWebsites]);

    // Handle template selection
    const handleSelectTemplate = async (template) => {
        setSubmittingId(template.id);
        try {
            // Save custom website selection via /api/event/theme-settings
            await eventThemeApi.saveThemeSettings({
                custom_website_id: template.id
            });

            setActiveWebsiteId(template.id);
            toast.success(`Tovuti ya "${template.name}" imechaguliwa kikamilifu!`);
        } catch (err) {
            console.error("Error saving website template selection:", err);
            const errMsg = err?.response?.data?.message || "Imeshindwa kuhifadhi muundo wa tovuti. Tafadhali jaribu tena.";
            toast.error(errMsg);
        } finally {
            setSubmittingId(null);
        }
    };

    return (
        <Container fluid className="templates-container py-4 px-2 px-md-4">
            {/* Page Header */}
            <div className="text-center mb-4">
                <h1 className="stylish-heading mb-2" style={{ color: '#4169e1', fontWeight: 700 }}>
                    Website Templates
                </h1>
                <p className="text-muted small mb-0" style={{ maxWidth: '650px', margin: '0 auto', fontSize: '0.95rem' }}>
                    Chagua muundo bora wa tovuti (custom website template) wa shughuli yako.
                </p>

                {activeWebsiteId && (
                    <div className="mt-3">
                        <Badge bg="success" className="px-3 py-2 rounded-pill fs-6 fw-normal d-inline-flex align-items-center gap-2 shadow-sm">
                            <FaCheckCircle /> Customized Website Active (ID: #{activeWebsiteId})
                        </Badge>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                    <p className="mt-3 text-muted fw-semibold">Inapakia website templates...</p>
                </div>
            ) : error ? (
                <Alert variant="danger" className="text-center my-4 rounded-3 shadow-sm">
                    {error}
                    <div className="mt-2">
                        <Button variant="outline-danger" size="sm" onClick={fetchCustomWebsites}>
                            Jaribu Tena (Retry)
                        </Button>
                    </div>
                </Alert>
            ) : templates.length === 0 ? (
                <div className="text-center py-5 text-muted bg-light rounded-4 border">
                    <h5 className="fw-bold text-dark">Hakuna Custom Website Templates Zilizopatikana</h5>
                    <Button variant="outline-primary" size="sm" className="rounded-pill px-4" onClick={fetchCustomWebsites}>
                        Refresh List
                    </Button>
                </div>
            ) : (
                <Row className="g-4">
                    {templates.map((template) => {
                        const isActive = String(template.id) === String(activeWebsiteId);
                        const isSubmitting = submittingId === template.id;

                        return (
                            <Col lg={4} md={6} xs={12} key={template.id}>
                                <Card className={`template-card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative ${isActive ? 'active-template' : ''}`}>
                                    {/* Badges Overlay */}
                                    <div className="position-absolute top-0 end-0 m-3 d-flex flex-column align-items-end gap-1" style={{ zIndex: 2 }}>
                                        {isActive ? (
                                            <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm fw-bold d-flex align-items-center gap-1">
                                                <FaCheckCircle size={12} /> Active Website
                                            </Badge>
                                        ) : (
                                            <Badge bg="primary" className="px-3 py-2 rounded-pill shadow-sm bg-opacity-90 fw-medium" style={{ backgroundColor: '#4169e1' }}>
                                                <FaStar className="me-1 mb-1" size={11} /> {template.tag}
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Preview Image */}
                                    {template.image ? (
                                        <div className="template-image-wrapper overflow-hidden bg-light position-relative">
                                            <Card.Img
                                                variant="top"
                                                src={template.image}
                                                alt={`${template.name} preview`}
                                                className="template-image"
                                            />
                                        </div>
                                    ) : (
                                        <div className="template-image-wrapper overflow-hidden bg-light position-relative d-flex align-items-center justify-content-center text-muted">
                                            <FaGlobe size={44} className="opacity-40 text-primary" />
                                        </div>
                                    )}

                                    <Card.Body className="d-flex flex-column p-4">
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="text-muted small fw-medium">
                                                    <FaPalette className="me-1 text-primary" /> {template.category}
                                                </span>

                                                {template.preview_url && (
                                                    <a
                                                        href={template.preview_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary text-decoration-none small d-inline-flex align-items-center gap-1"
                                                    >
                                                        <FaExternalLinkAlt size={11} /> Live Demo
                                                    </a>
                                                )}
                                            </div>

                                            <Card.Title className="template-name text-dark fw-bold mb-2 fs-5">
                                                {template.name}
                                            </Card.Title>

                                            {template.description && (
                                                <p className="text-muted small mb-0 text-truncate-2" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                                                    {template.description}
                                                </p>
                                            )}
                                        </div>

                                        <div className="mt-auto">
                                            <Button
                                                className="w-100 rounded-pill py-2.5 fw-semibold border-0 d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                                onClick={() => handleSelectTemplate(template)}
                                                disabled={isSubmitting || isActive}
                                                style={{ backgroundColor: isActive ? '#28a745' : '#4169e1' }}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <FaSpinner className="fa-spin" size={14} /> Inahifadhi...
                                                    </>
                                                ) : isActive ? (
                                                    <>
                                                        <FaCheck size={14} /> Customized Website Active
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaMagic size={14} /> Chagua Website Template
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}
        </Container>
    );
};

export default WebsiteTemplates;